import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  FeedState, 
  FeedTypesResponse, 
  FeedListResponse, 
  CreateFeedResponse,
  CreateFeedRequest,
  FeedListParams
} from '@/constants/types';
import { feedService } from '@/services/feed/feedService';
import { likesService, LikeToggleRequest } from '@/services/likes/likesService';

const initialState: FeedState = {
  feedTypes: [],
  feeds: [],
  pagination: null,
  isLoading: false,
  isFeedsLoading: false,
  isCreating: false,
  error: null,
};

export const fetchFeedTypes = createAsyncThunk(
  'feed/fetchFeedTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await feedService.getFeedTypes();
      console.log(response)

      return response;
    } catch (error: unknown) {
      console.error('Error fetching feed types:', error);
      let errorMessage = 'Failed to fetch feed types';
      
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null && 'response' in error) {
        const apiError = error as any;
        errorMessage = apiError.response?.data?.message || errorMessage;
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchFeeds = createAsyncThunk(
  'feed/fetchFeeds',
  async (params: FeedListParams, { rejectWithValue }) => {
    try {
      const response = await feedService.getFeeds(params);
      console.log(response)

      return response;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch feeds';
      return rejectWithValue(errorMessage);
    }
  }
);

export const createFeed = createAsyncThunk(
  'feed/createFeed',
  async (feedData: CreateFeedRequest, { rejectWithValue }) => {
    try {
      console.log('createFeed thunk: sending request with data:', feedData);
      const response = await feedService.createFeed(feedData);
      console.log('createFeed thunk: received response:', response);
      return response;
    } catch (error: unknown) {
      console.log('createFeed thunk: error occurred:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create feed';
      return rejectWithValue(errorMessage);
    }
  }
);

export const loadMoreFeeds = createAsyncThunk(
  'feed/loadMoreFeeds',
  async (params: FeedListParams, { rejectWithValue }) => {
    try {
      const response = await feedService.getFeeds(params);
      console.log('loadMoreFeeds response:', response);
      return response;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load more feeds';
      return rejectWithValue(errorMessage);
    }
  }
);

// Toggle like on feed
export const toggleFeedLike = createAsyncThunk(
  'feed/toggleLike',
  async (data: LikeToggleRequest, { rejectWithValue, dispatch }) => {
    try {
      const response = await likesService.toggleLike(data);
      console.log('toggleFeedLike API response:', response);
      const result = { 
        feedId: data.feedId,
        data: response.data // Extract the data from the API response
      };
      console.log('toggleFeedLike result:', result);
      dispatch(updateFeedStats(data?.feedId));
      return result;
    } catch (error: unknown) {
      console.error('toggleFeedLike error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to toggle like';
      return rejectWithValue(errorMessage);
    }
  }
);

// Get feed likes
export const getFeedLikes = createAsyncThunk(
  'feed/getFeedLikes',
  async (feedId: string, { rejectWithValue }) => {
    try {
      const response = await likesService.getFeedLikes(feedId);
      return response;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to get feed likes';
      return rejectWithValue(errorMessage);
    }
  }
);

// Update feed stats (after hover)
export const updateFeedStats = createAsyncThunk(
  'feed/updateFeedStats',
  async (feedId: string, { rejectWithValue }) => {
    try {
      const response = await feedService.getFeedDetails(feedId);
      console.log(response)
      return response.data.feed;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update feed stats';
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchMyFeeds = createAsyncThunk(
  'feed/fetchMyFeeds',
  async (params: FeedListParams, { rejectWithValue }) => {
    try {
      const response = await feedService.getMyFeeds(params);
      return response;
    } catch (error: unknown) {
      console.error('Error fetching my feeds:', error);
      let errorMessage = 'Failed to fetch your posts';

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null && 'response' in error) {
        const apiError = error as any;
        errorMessage = apiError.response?.data?.message || errorMessage;
      }

      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteFeed = createAsyncThunk(
  'feed/deleteFeed',
  async (feedId: string, { rejectWithValue }) => {
    try {
      await feedService.deleteFeed(feedId);
      return feedId;
    } catch (error: unknown) {
      console.error('Error deleting feed:', error);
      let errorMessage = 'Failed to delete post';

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null && 'response' in error) {
        const apiError = error as any;
        errorMessage = apiError.response?.data?.message || errorMessage;
      }

      return rejectWithValue(errorMessage);
    }
  }
);

const feedSlice = createSlice({
  name: 'feed',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetFeedState: () => initialState,
    setCreating: (state, action: PayloadAction<boolean>) => {
      state.isCreating = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Feed Types
      .addCase(fetchFeedTypes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFeedTypes.fulfilled, (state, action: PayloadAction<FeedTypesResponse>) => {
        state.isLoading = false;
        console.log(action.payload)
        state.feedTypes = action.payload.feedTypes;
        state.error = null;
      })
      .addCase(fetchFeedTypes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Feeds
      .addCase(fetchFeeds.pending, (state) => {
        state.isFeedsLoading = true;
        state.error = null;
      })
      .addCase(fetchFeeds.fulfilled, (state, action: PayloadAction<FeedListResponse>) => {
        state.isFeedsLoading = false;
        state.feeds = action.payload.feeds;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(fetchFeeds.rejected, (state, action) => {
        state.isFeedsLoading = false;
        state.error = action.payload as string;
      })
      // Create Feed
      .addCase(createFeed.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createFeed.fulfilled, (state, action: PayloadAction<CreateFeedResponse>) => {
        console.log('createFeed.fulfilled called', action.payload);
        state.isCreating = false;
        
        // Handle different response structures
        if (action.payload.data && action.payload.data.feed) {
          state.feeds.unshift(action.payload.data.feed);
        } else if ((action.payload as any).feed) {
          state.feeds.unshift((action.payload as any).feed);
        } else {
          console.error('Unexpected response structure:', action.payload);
        }
        
        state.error = null;
      })
      .addCase(createFeed.rejected, (state, action) => {
        console.log('createFeed.rejected called', action.payload);
        state.isCreating = false;
        state.error = action.payload as string;
      })
      // Load More Feeds
      .addCase(loadMoreFeeds.pending, (state) => {
        state.isFeedsLoading = true;
        state.error = null;
      })
      .addCase(loadMoreFeeds.fulfilled, (state, action: PayloadAction<FeedListResponse>) => {
        state.isFeedsLoading = false;
        // Append new feeds to existing ones
        state.feeds = [...state.feeds, ...action.payload.feeds];
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(loadMoreFeeds.rejected, (state, action) => {
        state.isFeedsLoading = false;
        state.error = action.payload as string;
      })
      // Toggle Feed Like
      .addCase(toggleFeedLike.fulfilled, (state, action) => {
        console.log('toggleFeedLike.fulfilled payload:', action.payload);
        const { feedId, data } = action.payload as any;
        const feedIndex = state.feeds.findIndex(feed => feed._id === feedId);
        console.log('Feed Index:', feedIndex, 'FeedId:', feedId);
        if (feedIndex !== -1) {
          console.log('Before update:', {
            isLiked: state.feeds[feedIndex].isLiked,
            likesCount: state.feeds[feedIndex].likesCount,
            statsLikes: state.feeds[feedIndex].stats?.likes
          });
          
          // Map API response to our state structure
          // API returns: { isLiked, totalLikes, likeChange, targetType, targetId, userId }
          state.feeds[feedIndex].isLiked = data?.isLiked ?? false;
          state.feeds[feedIndex].isMeLiked = data?.isLiked ?? false;
          state.feeds[feedIndex].likesCount = data?.totalLikes ?? 0;
          // Also update stats if available
          if (state.feeds[feedIndex].stats) {
            state.feeds[feedIndex].stats!.likes = data?.totalLikes ?? 0;
          }
          
          console.log('After update:', {
            isLiked: state.feeds[feedIndex].isLiked,
            likesCount: state.feeds[feedIndex].likesCount,
            statsLikes: state.feeds[feedIndex].stats?.likes
          });
        } else {
          console.error('Feed not found in state!', feedId);
        }
      })
      .addCase(toggleFeedLike.rejected, (state, action) => {
        console.error('toggleFeedLike.rejected:', action.payload);
        state.error = action.payload as string;
      })
      // Get Feed Likes
      .addCase(getFeedLikes.fulfilled, (state, action) => {
        console.log('getFeedLikes.fulfilled payload:', action.payload);
        const { feedId, isMeLiked, likesCount, likedUsers } = action.payload.data;
        const feedIndex = state.feeds.findIndex(feed => feed._id === feedId);
        if (feedIndex !== -1) {
          state.feeds[feedIndex].isLiked = isMeLiked;
          state.feeds[feedIndex].isMeLiked = isMeLiked;
          state.feeds[feedIndex].likesCount = likesCount;
          state.feeds[feedIndex].likedUsers = likedUsers;
          // Also update stats if available
          if (state.feeds[feedIndex].stats) {
            state.feeds[feedIndex].stats!.likes = likesCount;
          }
        }
      })
      // Update Feed Stats
      .addCase(updateFeedStats.fulfilled, (state, action) => {
        const updatedFeed = action.payload;
        const feedIndex = state.feeds.findIndex(feed => feed._id === updatedFeed._id);
        console.log(updatedFeed)
        if (feedIndex !== -1) {
          // Preserve like state before updating
          // const existingLikeState = {
          //   isLiked: state.feeds[feedIndex].isLiked,
          //   isMeLiked: state.feeds[feedIndex].isMeLiked,
          //   likesCount: state.feeds[feedIndex].likesCount,
          //   likedUsers: state.feeds[feedIndex].likedUsers
          // };
          
          // Update the entire feed
          state.feeds[feedIndex] = updatedFeed;
          
          // // Restore like state if it existed
          // if (existingLikeState.isLiked !== undefined) {
          //   state.feeds[feedIndex].isLiked = existingLikeState.isLiked;
          // }
          // if (existingLikeState.isMeLiked !== undefined) {
          //   state.feeds[feedIndex].isMeLiked = existingLikeState.isMeLiked;
          // }
          // if (existingLikeState.likesCount !== undefined) {
          //   state.feeds[feedIndex].likesCount = existingLikeState.likesCount;
          // }
          // // if (existingLikeState.likedUsers !== undefined) {
          //   state.feeds[feedIndex].likedUsers = existingLikeState.likedUsers;
          // // }
        }
      })
      // Fetch My Feeds
      .addCase(fetchMyFeeds.pending, (state) => {
        state.isFeedsLoading = true;
        state.error = null;
      })
      .addCase(fetchMyFeeds.fulfilled, (state, action) => {
        state.isFeedsLoading = false;
        state.feeds = action.payload.feeds;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchMyFeeds.rejected, (state, action) => {
        state.isFeedsLoading = false;
        state.error = action.payload as string;
      })
      // Delete Feed
      .addCase(deleteFeed.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteFeed.fulfilled, (state, action) => {
        state.isLoading = false;
        state.feeds = state.feeds.filter(feed => feed._id !== action.payload);
        state.error = null;
      })
      .addCase(deleteFeed.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, resetFeedState, setCreating } = feedSlice.actions;
export default feedSlice.reducer;